import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api.js';

const EMPTY = {
  relationship: {
    health: null,
    healthNotes: [],
    lastMeaningfulInteraction: null,
    lastStrategicReview: null,
    nextCheckpoint: '',
    contactCadence: '',
    updatedAt: null,
  },
  contacts: [],
  actions: [],
  opportunities: [],
  platformOrgs: [],
  assignedUsers: [],
};

// Loads the per-client Intelligence payload (Info / Outlook data). Kept out of
// `/bootstrap` on purpose — heavy, per-client, only needed once a client opens.
//
// `loaded` is false until the first successful fetch: the Info editors must not
// run until then, or a replace-all save from the empty fallback would erase
// contacts that just hadn't arrived yet.
export function useClientDetail(clientId) {
  const [detail, setDetail] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  // Newest-fetch id — a slower earlier GET must not apply its stale snapshot.
  const gen = useRef(0);

  const apply = (data) =>
    setDetail({
      ...EMPTY,
      ...data,
      relationship: { ...EMPTY.relationship, ...data.relationship },
    });

  const load = useCallback(
    async ({ rethrow }) => {
      const mine = (gen.current += 1);
      setLoading(true);
      try {
        const data = await api(`/clients/${clientId}/detail`);
        if (mine !== gen.current) return;
        apply(data);
        setLoaded(true);
        setError('');
      } catch (err) {
        if (mine === gen.current) setError(err.message);
        // A refresh after a successful mutation must surface its failure so the
        // editor stays open instead of leaving a stale snapshot on screen.
        if (rethrow) throw err;
      } finally {
        if (mine === gen.current) setLoading(false);
      }
    },
    [clientId],
  );

  const reload = useCallback(() => load({ rethrow: true }), [load]);

  useEffect(() => {
    setDetail(EMPTY);
    setLoaded(false);
    load({ rethrow: false }).catch(() => {});
    return () => {
      gen.current += 1;
    };
  }, [clientId, load]);

  return { detail, loading, loaded, error, reload };
}
